<template>
	<SimpleModalComponent
		:heading="`Add ${userType}`"
		@close-modal="closeModal"
	>
		<template #content>
			<template
				v-for="user, index in users"
				:key="'user-' + index"
			>
				<div class="flex flex--align-center flex--gap-medium">
					<div class="form__set">
						<label
							:for="'user-' + index"
							class="form__label"
							:class="{ 'invisible': index > 0 }"
						>
							{{ input_label }}
						</label>

						<div
							v-if="filtered_user_list && filtered_user_list.length"
							class="form__icon form__icon--down"
						>
							<select
								:id="'user-' + index"
								v-model="user.value"
								class="form__select form-select__stretch"
								:class="{ 'form__select--error': errors[ user.id ] }"
							>
								<option
									v-for="available_user in filtered_user_list"
									:key="`submission-${ available_user.value }`"
									:value="available_user.value"
									:disabled="!available_user.value"
								>
									{{ available_user.label }}
								</option>
							</select>
						</div>
						<div v-else>
							<p>
								No users found
							</p>
						</div>
					</div>

					<button
						v-if="index > 0"
						class="icon icon--close mb-16"
						@click="removeUser( index )"
					/>
				</div>

				<div
					v-if="errors[ user.id ]"
					class="form__error"
				>
					<p>
						{{ errors[ user.id ] }}
					</p>
				</div>
			</template>

			<button
				v-if="filtered_user_list && filtered_user_list.length && can_add_multiple"
				class="flex flex--align-center flex--gap-small icon icon--add p--small"
				@click="addUser"
			>
				{{ add_another_label }}
			</button>

			<p
				v-if="errorMessage"
				class="error-message"
			>
				{{ errorMessage }}
			</p>
		</template>

		<template #buttons>
			<button
				class="button"
				:disabled="loading || !filtered_user_list || !filtered_user_list.length"
				@click="closeModal"
			>
				Cancel
			</button>
			<button
				class="button button--fill"
				:class="{ 'button--loading' : loading }"
				:disabled="loading || !filtered_user_list || !filtered_user_list.length"
				@click="addSelectedUsersToProject"
			>
				Add {{ userType }}
			</button>
		</template>
	</SimpleModalComponent>
</template>

<script setup>
import SimpleModalComponent from './SimpleModalComponent.vue';

import { ref, computed, defineProps, defineEmits } from 'vue';
import {
	USER_TYPE_BUSINESS_DEVELOPMENT,
	USER_TYPE_client_services,
	USER_TYPE_PROJECT_MANAGER,
	USER_TYPE_LEAD_ARTIST,
	USER_TYPE_ARTIST,
	USER_TYPE_CLIENT
} from '../../constants';

const props = defineProps( {
	project: {
		required: true,
		type: Object
	},
	userList: {
		required: true,
		type: Array
	},
	userType: {
		required: true,
		type: String
	},
	loading: {
		required: true,
		type: Boolean
	},
	errors: {
		required: false,
		type: Array,
		default: null
	},
	errorMessage: {
		required: false,
		type: String,
		default: ''
	}
} );

const emit = defineEmits( ['add-users-to-project', 'close-modal'] );

const users = ref( [
	{
		id: `user-${Date.now()}`,
		value: null
	}
] );

const filtered_user_list = computed( () => {
	if ( props.userType === USER_TYPE_ARTIST ) {
		return props.userList.filter( user => !props.project.project_artists.find( artist => artist.id === user.value ) );
	}
	if ( props.userType === USER_TYPE_CLIENT ) {
		return props.userList.filter( user => !props.project.clients.find( client => client.id === user.value ) );
	}
	return props.userList;
} );

const input_label = computed( () => {
	switch ( props.userType ) {
		case USER_TYPE_BUSINESS_DEVELOPMENT:
			return 'Select a business development director';
		case USER_TYPE_client_services:
			return 'Select a client services';
		case USER_TYPE_LEAD_ARTIST:
			return 'Select a lead artist';
		case USER_TYPE_PROJECT_MANAGER:
			return 'Select a project manager';
		case USER_TYPE_ARTIST:
			return 'Select one or more artists';
		case USER_TYPE_CLIENT:
			return 'Select one or more clients or partners';
		default:
			return 'Select a user';
	}
} );

const add_another_label = computed( () => {
	if ( props.userType === USER_TYPE_ARTIST ) {
		return 'Add another artist';
	}
	if ( props.userType === USER_TYPE_CLIENT ) {
		return 'Add another client or partner';
	}
	return null;
} );

const can_add_multiple = computed( () => {
	return props.userType === USER_TYPE_ARTIST || props.userType === USER_TYPE_CLIENT;
} );

const addUser = () => { // eslint-disable-line snakecasejs/snakecasejs
	users.value.push( {
		id: `user-${Date.now()}`,
		value: null
	} );
};
const removeUser = index => { // eslint-disable-line snakecasejs/snakecasejs
	users.value.splice( index, 1 );
};
const closeModal = () => { // eslint-disable-line snakecasejs/snakecasejs
	emit( 'close-modal' );
};
const addSelectedUsersToProject = () => { // eslint-disable-line snakecasejs/snakecasejs
	emit( 'add-users-to-project', users.value );
};
</script>